import React from 'react';
import { Box, Divider, Image, InputGroup, InputRightElement, Text } from '@chakra-ui/react';
import TwygsButton from '../../../../components/Button/index';
import { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { passwordSchema } from '../../../../components/Settings/schema.js';
import CustomModal from '../../../../components/Modal/CustomModal/index.jsx';
import SuccessfulIcon from '../../../../assets/done-circle-icon.svg';
import { InputHookForm } from '../../../../components/Common/Form/input-with-hook-form';
import { IFormPassword } from '../../../../components/Profile/profile.type';
import IntroSection from '../../../../components/Profile/intro-section/intro-section.component.jsx';
import EyeOff from '../../../../assets/eye-off.svg';
import EyeOn from '../../../../assets/eye-disabled-icon.svg';
import { updatePassword } from '../../../../apis/settings.api';
import ChangeTwoFactorAuthentication from '../../../../components/Profile/change-two-factor-authentication/change-two-factor-authentication.component.jsx';
import DataLayer from '../../../../utils/data-layer';
interface ChangePasswordProps {
  profileData?: any;
}

export const PasswordSettings: React.FC<ChangePasswordProps> = ({ profileData }) => {
  const [showPw, setShowPw] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const [openModal, setOpenModal] = useState(false);

  const methods = useForm({
    resolver: yupResolver(passwordSchema),
    mode: 'onChange'
  });

  const clearState = useRef(false);
  const cfFocused = useRef(false);

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false
  });

  const passwordInput = useRef({});
  passwordInput.current = methods.watch('newPassword', '');

  const confirmInput = useRef({});
  confirmInput.current = methods.watch('confirmPassword', '');

  useEffect(() => {
    const password = `${passwordInput.current}`;

    setPasswordRequirements({
      length: password.length >= 8 && password.length <= 64,
      uppercase: /[A-Z]/.test(password) && /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*()_+}{"':;?/>.<,]/.test(password)
    });

    if (!clearState.current && cfFocused.current) {
      methods.trigger('confirmPassword');
    } else {
      clearState.current = false;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordInput.current, methods.trigger]);

  const onSubmit = async (data: IFormPassword) => {
    try {
      await updatePassword(data);
      DataLayer.trackUpdatePassword();
      setOpenModal(true);
      clearState.current = true;
      cfFocused.current = false;
      methods.reset();
    } catch (err: any) {
      if (err?.response?.data?.statusCode === 400) {
        if (err?.response?.data?.message === 'New password must be difference')
          methods.setError('newPassword', {
            type: 'manual',
            message: 'New password must be different from current password'
          });
        else {
          methods.setError('currentPassword', {
            type: 'manual',
            message: err?.response?.data?.message
          });
        }
      }
    }
  };

  const renderModal = () => {
    return (
      <CustomModal
        isOpen
        title="Success!"
        onClick={() => setOpenModal(false)}
        hideFooter
        maxW="407px"
        p={'32px'}
      >
        <div className="w-full flex flex-col items-center">
          <img src={SuccessfulIcon} alt="" />
          <p className="text-lg font-[600] mt-4 text-center px-6">Password updated successfully!</p>
          <div className="flex gap-[10px] mt-6 w-full">
            <TwygsButton
              variant="primary"
              className="!text-white hover:opacity-80 px-5 py-[10px] flex items-center justify-center rounded-[8px]  w-full text-lg font-[900]"
              onClick={() => setOpenModal(false)}
              h={'42px'}
            >
              Done
            </TwygsButton>
          </div>
        </div>
      </CustomModal>
    );
  };

  if (!profileData?.email) {
    return null;
  }

  return (
    <div className="w-full overflow-y-auto">
      <Box>
        <IntroSection
          title="Password"
          description="Please enter your current password to change your password."
          titleSize="text-2xl"
          desSize="text-base"
        />
        <Divider my="1.25rem" color="anti-flash-white" opacity={1} />
        <Box mt={{ base: '1.5rem', md: '0' }} maxW={'100%'}>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <Box
                display="flex"
                alignItems={{ base: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                maxW={'100%'}
                flexDirection={{ base: 'column', md: 'row' }}
                gap={{ base: '0.75rem', md: '0rem' }}
              >
                <Text
                  fontWeight={600}
                  color="jet-black"
                  w={{ base: '100%', md: '35%' }}
                  fontSize={'sm'}
                >
                  Current password
                </Text>
                <Box
                  w={{ base: '100%', md: '65%' }}
                  display="flex"
                  gap={{ base: '0.75rem', md: '1.5rem' }}
                  flexDirection={{ base: 'column', md: 'row' }}
                >
                  <InputGroup rounded="0.75rem">
                    <InputHookForm
                      bg="light.100 "
                      p={{ base: '8px 12px', md: '10px 14px' }}
                      rounded="8px"
                      type={showPw.currentPassword ? 'text' : 'password'}
                      placeholder="Enter current password"
                      name="currentPassword"
                    />
                    <InputRightElement h={{ base: '38px', md: '42px' }} pr="16px">
                      <Image
                        w="16px"
                        alt=""
                        src={showPw.currentPassword ? EyeOn : EyeOff}
                        onClick={() => {
                          setShowPw({
                            ...showPw,
                            currentPassword: !showPw.currentPassword
                          });
                        }}
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </Box>

              <Divider my="1.25rem" color="anti-flash-white" opacity={1} />

              <Box
                display="flex"
                alignItems={{ base: 'flex-start', md: 'flex-start' }}
                justifyContent="space-between"
                maxW={'100%'}
                flexDirection={{ base: 'column', md: 'row' }}
                gap={{ base: '0.75rem', md: '0rem' }}
              >
                <Text
                  fontWeight={600}
                  color="jet-black"
                  w={{ base: '100%', md: '35%' }}
                  fontSize={'sm'}
                >
                  New password
                </Text>
                <Box
                  w={{ base: '100%', md: '65%' }}
                  display="flex"
                  gap={{ base: '0.25rem', md: '0.25rem' }}
                  flexDirection={{ base: 'column', md: 'column' }}
                >
                  <InputGroup rounded="0.75rem">
                    <InputHookForm
                      bg="light.100 "
                      p={{ base: '8px 12px', md: '10px 14px' }}
                      rounded="8px"
                      maxLength={64}
                      type={showPw.newPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      name="newPassword"
                    />
                    <InputRightElement h={{ base: '38px', md: '42px' }} pr="1rem">
                      <Image
                        w="16px"
                        alt=""
                        src={showPw.newPassword ? EyeOn : EyeOff}
                        onClick={() => {
                          setShowPw({
                            ...showPw,
                            newPassword: !showPw.newPassword
                          });
                        }}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <Text color={'dim-gray'} fontSize={{ base: 'xs', md: 'sm' }}>
                    Password must include at least 8-12 characters, a number, an uppercase, a
                    lowercase and a special character.
                  </Text>
                </Box>
              </Box>

              <Divider my="1.25rem" color="anti-flash-white" opacity={1} />

              <Box
                display="flex"
                alignItems={{ base: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                maxW={'100%'}
                flexDirection={{ base: 'column', md: 'row' }}
                gap={{ base: '0.75rem', md: '0rem' }}
              >
                <Text
                  fontWeight={600}
                  color="jet-black"
                  w={{ base: '100%', md: '35%' }}
                  fontSize={'sm'}
                >
                  Confirm new password
                </Text>
                <Box
                  w={{ base: '100%', md: '65%' }}
                  display="flex"
                  gap={{ base: '0.75rem', md: '1.5rem' }}
                  flexDirection={{ base: 'column', md: 'row' }}
                >
                  <InputGroup rounded="0.75rem">
                    <InputHookForm
                      bg="light.100"
                      p={{ base: '8px 12px', md: '10px 14px' }}
                      rounded="8px"
                      type={showPw.confirmPassword ? 'text' : 'password'}
                      placeholder="Enter confirm password"
                      name="confirmPassword"
                      onFocus={() => (cfFocused.current = true)}
                    />
                    <InputRightElement h={{ base: '38px', md: '42px' }} pr="1rem">
                      <Image
                        w="16px"
                        alt=""
                        src={showPw.confirmPassword ? EyeOn : EyeOff}
                        onClick={() => {
                          setShowPw({
                            ...showPw,
                            confirmPassword: !showPw.confirmPassword
                          });
                        }}
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </Box>

              <Divider my="1.25rem" color="anti-flash-white" opacity={1} />

              <Box
                display="flex"
                justifyContent={'flex-end'}
                alignContent={'center'}
                alignItems={{ base: 'flex-start', md: 'center' }}
                maxW={'100%'}
                flexDirection={'row'}
                gap={'12px'}
              >
                <TwygsButton
                  onClick={() => {
                    clearState.current = true;
                    cfFocused.current = false;
                    methods.reset();
                  }}
                  variant="outline"
                  className="h-10"
                >
                  Cancel
                </TwygsButton>
                <TwygsButton type="submit" variant="primary" className="h-10">
                  Update Password
                </TwygsButton>
              </Box>
            </form>
          </FormProvider>
          <Box mt={'40px'}>
            <ChangeTwoFactorAuthentication profileData={profileData} />
          </Box>
          {openModal && renderModal()}
        </Box>
      </Box>
    </div>
  );
};
